/* ═════════════════════════ SponsoredNativeAd ══════════════════════════════
   Headless lifecycle component. Renders nothing to the DOM.

   • Installed app → shows the 300×250 AdMob banner exactly over the gold ad
     frame (`anchorRef`) while the Inscribe tab is visible and no tile is open.
     If the page scrolls, the banner hides while moving and re-lands on the frame.
   • Web / preview → no-op (the empty gold frame stays visible).
   ─────────────────────────────────────────────────────────────────────────── */

import { useEffect } from 'react';
import { showAdMobBanner, removeAdMobBanner, isAdMobSupported } from '../../config/admob';

export const SponsoredNativeAd = ({ enabled = true, anchorRef = null }) => {
  const adMobNative = isAdMobSupported();

  useEffect(() => {
    if (!enabled || !adMobNative) return undefined;
    let cancelled = false;
    let shownTop = null;
    let timer = null;

    const place = async () => {
      if (cancelled) return;
      const rect = anchorRef?.current?.getBoundingClientRect();
      if (!rect || rect.height <= 0) return;
      const visible = rect.top >= 0 && rect.top + 250 <= window.innerHeight;
      if (!visible) {
        if (shownTop != null) { shownTop = null; await removeAdMobBanner(); }
        return;
      }
      if (shownTop != null && Math.abs(shownTop - rect.top) < 4) return;
      if (shownTop != null) await removeAdMobBanner();
      if (cancelled) return;
      shownTop = rect.top;
      await showAdMobBanner({ top: rect.top });
      if (cancelled) removeAdMobBanner();
    };

    const schedule = (delay) => { clearTimeout(timer); timer = setTimeout(place, delay); };
    const onMove = () => {
      const rect = anchorRef?.current?.getBoundingClientRect();
      if (shownTop != null && rect && Math.abs(shownTop - rect.top) >= 4) {
        shownTop = null;
        removeAdMobBanner();
      }
      schedule(300);
    };

    schedule(350);
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
      removeAdMobBanner();
    };
  }, [enabled, adMobNative, anchorRef]);

  return null;
};

export default SponsoredNativeAd;
